import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  Card,
  Button,
  Icon,
  Typography,
  Row,
  Col,
  Progress,
  Divider,
  Alert,
  Slider,
  Input,
  Tag,
  Tooltip,
  Empty,
  Modal
} from 'antd'
import { useMusic } from '../../hooks'
import VideoHistoryModal from './VideoHistoryModal'
import { VideoPlayer } from '../../../../components'

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input

const FinalVideoDisplay = ({ formData, setFormData, videoId }) => {
  const { finalVideo } = formData || {}
  const [musicVolume, setMusicVolume] = useState(0.4)
  const [userPrompt, setUserPrompt] = useState('')
  const [currentVideoUrl, setCurrentVideoUrl] = useState(finalVideo)
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const previewRef = useRef(null)

  const {
    generatePreview,
    generateFinal,
    previewData,
    finalData,
    isPolling,
    progress,
    status,
    message,
    error,
    generationType
  } = useMusic({ videoId })

  // Update current video URL when final data is available
  useEffect(() => {
    if (finalData?.mixed_video_s3_url) {
      setCurrentVideoUrl(finalData.mixed_video_s3_url)
      if (setFormData) {
        setFormData(prev => {
          const newHistoryEntry = {
            date: new Date().toISOString(),
            url: finalData.mixed_video_s3_url,
            hasMusic: true,
            musicAnalysis: previewData // The last AI analysis data
          }
          const existingHistory = Array.isArray(prev.final_videos_history)
            ? prev.final_videos_history
            : []
          const updatedHistory = [...existingHistory, newHistoryEntry]

          return {
            ...prev,
            finalVideo: finalData.mixed_video_s3_url,
            musicData: finalData,
            final_videos_history: updatedHistory
          }
        })
      }
    }
  }, [finalData, previewData, setFormData])

  // Parse reasoning from transition config
  const reasoning = useMemo(() => {
    if (!previewData?.transition_config_json) return null
    try {
      const config =
        typeof previewData.transition_config_json === 'string'
          ? JSON.parse(previewData.transition_config_json)
          : previewData.transition_config_json
      return config.reasoning
    } catch (e) {
      return null
    }
  }, [previewData])

  // Scroll preview into view when it becomes available
  useEffect(() => {
    if (previewData && !isPolling && generationType === 'preview') {
      const timer = setTimeout(() => {
        previewRef.current &&
          previewRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
      }, 500) // Small delay to ensure DOM is updated and rendered
      return () => clearTimeout(timer)
    }
  }, [previewData, isPolling, generationType])

  if (!finalVideo) {
    return (
      <div style={{ padding: '100px 0', textAlign: 'center' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span>
              No video generated yet. <br />
              <Text type="secondary">
                Complete the previous steps to see your masterpiece.
              </Text>
            </span>
          }
        />
      </div>
    )
  }

  const handleGeneratePreview = async () => {
    try {
      await generatePreview(finalVideo, { musicVolume, userPrompt })
    } catch (err) {
      console.error('Failed to generate preview:', err)
    }
  }

  const handleGenerateFinal = async () => {
    if (!previewData?.analysis_id) return
    try {
      await generateFinal(previewData.analysis_id, { musicVolume })
    } catch (err) {
      console.error('Failed to generate final music:', err)
    }
  }

  return (
    <div
      className="final-video-display"
      style={{ width: '100%', padding: '24px' }}
    >
      <VideoHistoryModal
        visible={showHistoryModal}
        onCancel={() => setShowHistoryModal(false)}
        history={formData.final_videos_history}
        onSelect={url => {
          setCurrentVideoUrl(url)
          setShowHistoryModal(false)
          if (setFormData) {
            setFormData({
              ...formData,
              finalVideo: url
            })
          }
        }}
      />
      <Row gutter={[24, 24]}>
        {/* Left Column: Video & Insights */}
        <Col xs={24} lg={16}>
          <Card
            bordered={false}
            bodyStyle={{ padding: 0 }}
            style={{
              overflow: 'hidden',
              borderRadius: '16px',
              backgroundColor: '#fff',
              boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
              marginBottom: 24
            }}
          >
            <div
              style={{
                position: 'relative',
                paddingTop: '56.25%' /* 16:9 Aspect Ratio */,
                background: '#0a0a0a'
              }}
            >
              <VideoPlayer
                key={currentVideoUrl}
                src={currentVideoUrl}
                controls
                autoPlay
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            </div>

            <div style={{ padding: '32px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <Title
                    level={3}
                    style={{
                      margin: 0,
                      fontWeight: 700,
                      letterSpacing: '-0.5px'
                    }}
                  >
                    Final Result
                  </Title>
                  <Text type="secondary" style={{ fontSize: '14px' }}>
                    Crafted with AI, tailored for your brand.
                  </Text>
                </div>
                <Button
                  type="primary"
                  onClick={() => setShowHistoryModal(true)}
                  icon="history"
                  shape="round"
                >
                  Version History
                </Button>
              </div>

              {previewData && (
                <div className="video-insights">
                  <Divider orientation="left" style={{ margin: '32px 0 20px' }}>
                    <Text
                      strong
                      style={{
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px',
                        fontSize: '11px',
                        opacity: 0.5
                      }}
                    >
                      AI Video Insights
                    </Text>
                  </Divider>

                  <Row gutter={[32, 20]}>
                    <Col span={24}>
                      <Text
                        strong
                        style={{
                          fontSize: '14px',
                          display: 'block',
                          marginBottom: 8,
                          color: '#000',
                          opacity: 0.8
                        }}
                      >
                        Description
                      </Text>
                      <Paragraph
                        style={{
                          fontSize: '15px',
                          lineHeight: '1.6',
                          color: '#555',
                          margin: 0
                        }}
                      >
                        {previewData.video_description}
                      </Paragraph>
                    </Col>

                    <Col xs={24} md={12}>
                      <Text
                        strong
                        style={{
                          fontSize: '14px',
                          display: 'block',
                          marginBottom: 12,
                          color: '#000',
                          opacity: 0.8
                        }}
                      >
                        Atmosphere
                      </Text>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px'
                        }}
                      >
                        {previewData.mood?.split(',').map(m => (
                          <Tag
                            key={m}
                            color="blue"
                            style={{
                              borderRadius: '4px',
                              padding: '2px 10px',
                              fontSize: '12px',
                              border: 'none',
                              background: '#f0f7ff'
                            }}
                          >
                            {m.trim()}
                          </Tag>
                        ))}
                        <Tag
                          color="cyan"
                          style={{
                            borderRadius: '4px',
                            padding: '2px 10px',
                            fontSize: '11px',
                            fontWeight: 600,
                            border: 'none',
                            background: '#e6fffb'
                          }}
                        >
                          {previewData.tempo?.toUpperCase()} TEMPO
                        </Tag>
                      </div>
                    </Col>

                    <Col xs={24} md={12}>
                      <Text
                        strong
                        style={{
                          fontSize: '14px',
                          display: 'block',
                          marginBottom: 12,
                          color: '#000',
                          opacity: 0.8
                        }}
                      >
                        Suggested Genres
                      </Text>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px'
                        }}
                      >
                        {previewData.genre_suggestions?.map(g => (
                          <Tag
                            key={g}
                            color="purple"
                            style={{
                              borderRadius: '4px',
                              padding: '2px 10px',
                              fontSize: '12px',
                              border: 'none',
                              background: '#f9f0ff'
                            }}
                          >
                            {g}
                          </Tag>
                        ))}
                      </div>
                    </Col>

                    {reasoning && (
                      <Col span={24}>
                        <Alert
                          message={
                            <Text strong style={{ fontSize: '13px' }}>
                              AI Production Strategy
                            </Text>
                          }
                          description={
                            <Text style={{ fontSize: '13px' }}>
                              {reasoning}
                            </Text>
                          }
                          type="info"
                          showIcon
                          icon={<Icon type="bulb" />}
                          style={{
                            borderRadius: '12px',
                            border: 'none',
                            background: '#f8fbfc',
                            padding: '12px 16px 12px 52px'
                          }}
                        />
                      </Col>
                    )}
                  </Row>
                </div>
              )}
            </div>
          </Card>
        </Col>

        {/* Right Column: Music Enhancement Settings */}
        <Col xs={24} lg={8}>
          <Card
            bordered={false}
            title={
              <div style={{ padding: '8px 0' }}>
                <Title level={4} style={{ margin: 0 }}>
                  <Icon
                    type="customer-service"
                    style={{ marginRight: 12, color: '#1890ff' }}
                  />
                  Music Enhancement
                </Title>
              </div>
            }
            style={{
              borderRadius: '16px',
              backgroundColor: '#fff',
              boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
              position: 'sticky',
              top: 24
            }}
          >
            <div className="music-settings">
              <div style={{ marginBottom: 32 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 12
                  }}
                >
                  <Text strong style={{ fontSize: '15px' }}>
                    Music Volume
                  </Text>
                  <Text strong style={{ color: '#1890ff' }}>
                    {(musicVolume * 100).toFixed(0)}%
                  </Text>
                </div>
                <Slider
                  min={0}
                  max={1}
                  step={0.05}
                  value={musicVolume}
                  onChange={setMusicVolume}
                  tooltipVisible={false}
                  disabled={isPolling}
                  className="premium-slider"
                />
              </div>

              <div style={{ marginBottom: 32 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 12
                  }}
                >
                  <Text strong style={{ fontSize: '15px' }}>
                    Custom Music Style
                  </Text>
                  <Tooltip title="Describe the vibe, instruments, or genre you want. Leave empty for AI choice.">
                    <Icon
                      type="question-circle"
                      style={{ marginLeft: 8, opacity: 0.4 }}
                    />
                  </Tooltip>
                </div>
                <TextArea
                  rows={4}
                  placeholder="e.g., Emotional orchestral music with piano and strings, building up to an inspiring finish..."
                  value={userPrompt}
                  onChange={e => setUserPrompt(e.target.value)}
                  disabled={isPolling}
                  style={{
                    borderRadius: '12px',
                    padding: '12px',
                    borderColor: '#e8e8e8',
                    resize: 'none',
                    fontSize: '14px'
                  }}
                />
              </div>

              {isPolling && (
                <div
                  style={{
                    marginBottom: 32,
                    padding: '24px',
                    background: '#fafafa',
                    borderRadius: '12px'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 12
                    }}
                  >
                    <Text strong style={{ color: '#1890ff' }}>
                      <Icon type="loading" style={{ marginRight: 8 }} />
                      {generationType === 'preview'
                        ? 'Generating Preview with Music...'
                        : 'Assembling Final Video...'}
                    </Text>
                  </div>

                  {message && (
                    <div style={{ marginBottom: 12 }}>
                      <Text type="secondary" style={{ fontSize: '13px' }}>
                        {message}
                      </Text>
                    </div>
                  )}

                  <Progress
                    percent={progress}
                    status="active"
                    strokeWidth={10}
                    strokeColor={{
                      '0%': '#1890ff',
                      '100%': '#52c41a'
                    }}
                    style={{ marginBottom: 0 }}
                  />
                  <Text
                    type="secondary"
                    style={{
                      display: 'block',
                      marginTop: 12,
                      fontSize: '12px',
                      textAlign: 'center'
                    }}
                  >
                    This might take a minute. Sit tight!
                  </Text>
                </div>
              )}

              {error && (
                <Alert
                  message="Generation Error"
                  description={error}
                  type="error"
                  showIcon
                  style={{ marginBottom: 32, borderRadius: '12px' }}
                />
              )}

              <div style={{ display: 'grid', gap: '16px' }}>
                <Button
                  block
                  size="large"
                  onClick={handleGeneratePreview}
                  loading={isPolling && !previewData}
                  disabled={isPolling}
                  style={{
                    height: '56px',
                    borderRadius: '12px',
                    fontWeight: 600,
                    fontSize: '16px'
                  }}
                >
                  <Icon type="play-circle" />{' '}
                  {previewData
                    ? 'Regenerate Preview'
                    : 'Generate Video Preview with Music'}
                </Button>

                <Button
                  block
                  type="primary"
                  size="large"
                  onClick={handleGenerateFinal}
                  loading={isPolling && previewData && !finalData}
                  disabled={isPolling || !previewData}
                  style={{
                    height: '56px',
                    borderRadius: '12px',
                    fontWeight: 600,
                    fontSize: '16px',
                    background:
                      'linear-gradient(135deg, #1890ff 0%, #001529 100%)',
                    border: 'none',
                    boxShadow: !previewData
                      ? 'none'
                      : '0 8px 20px rgba(24, 144, 255, 0.3)'
                  }}
                >
                  <Icon type="thunderbolt" />{' '}
                  {finalData ? 'Regenerate Full Video' : 'Assemble Final Video'}
                </Button>
              </div>

              {previewData && !finalData && !isPolling && (
                <div ref={previewRef} style={{ marginTop: 32 }}>
                  <div style={{ marginBottom: 12 }}>
                    <Text strong style={{ fontSize: '15px' }}>
                      Preview Mix
                    </Text>
                  </div>
                  <div
                    style={{
                      position: 'relative',
                      paddingTop: '56.25%',
                      background: '#000',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                    }}
                  >
                    <video
                      src={previewData.preview_mixed_video_s3_url}
                      controls
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                  <Alert
                    message="Preview Ready"
                    description="Watch the 15s snippet above. If you're happy, assemble the full video."
                    type="success"
                    showIcon
                    style={{
                      marginTop: 16,
                      borderRadius: '12px',
                      padding: '12px 16px 12px 52px'
                    }}
                  />
                </div>
              )}

              {finalData && !isPolling && (
                <div style={{ marginTop: 32 }}>
                  <Alert
                    message="Production Complete"
                    description="Your high-fidelity video with synced background music is ready. Use the main player to review."
                    type="success"
                    showIcon
                    style={{
                      borderRadius: '12px',
                      padding: '16px 16px 16px 52px'
                    }}
                  />
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <style jsx global>{`
        .premium-slider .ant-slider-rail {
          background-color: #f0f0f0;
          height: 6px;
        }
        .premium-slider .ant-slider-track {
          background: linear-gradient(90deg, #1890ff 0%, #52c41a 100%);
          height: 6px;
        }
        .premium-slider .ant-slider-handle {
          width: 20px;
          height: 20px;
          margin-top: -8px;
          border: 4px solid #fff;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </div>
  )
}

export default FinalVideoDisplay
